import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  GetWorkspaceAdvancedPermissionsDocument,
  UpdateWorkspaceAdvancedPermissionsDocument,
} from "#app/lib/data";

// Apollo plumbing for the permissions page's Advanced Permissions
// section. Lives in lib/hooks/ per the consumer-app rule (Apollo
// stays out of component files). Exposes the current toggle values
// plus a single setter that writes optimistically so the Switch UI
// doesn't lag the mutation.

export type AdvancedPermissionsField =
  | "defaultPrivateWorkflows"
  | "adminOverrideEnabled";

export function useWorkspaceAdvancedPermissions(workspaceId: string) {
  const variables = useMemo(() => ({ id: workspaceId }), [workspaceId]);
  const { data, loading } = useQuery(
    GetWorkspaceAdvancedPermissionsDocument,
    { variables, skip: !workspaceId },
  );
  const [update] = useMutation(UpdateWorkspaceAdvancedPermissionsDocument);
  const ws = data?.workspace ?? null;

  const setField = useCallback(
    (field: AdvancedPermissionsField, next: boolean) => {
      if (!ws) return;
      void update({
        variables: { id: workspaceId, input: { [field]: next } },
        optimisticResponse: {
          updateWorkspace: {
            __typename: "Workspace" as const,
            id: ws.id,
            workspaceId: ws.workspaceId,
            adminOverrideEnabled:
              field === "adminOverrideEnabled" ? next : ws.adminOverrideEnabled,
            defaultPrivateWorkflows:
              field === "defaultPrivateWorkflows"
                ? next
                : ws.defaultPrivateWorkflows,
          },
        },
      });
    },
    [update, workspaceId, ws],
  );

  return {
    loading,
    defaultPrivateWorkflows: ws?.defaultPrivateWorkflows ?? false,
    adminOverrideEnabled: ws?.adminOverrideEnabled ?? false,
    ready: ws != null,
    setField,
  };
}
